import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Navigation.scss';
import NavTab, { NavTabId } from './NavTab';
import {
  ExploreNavIcon,
  ExploreNavIconFilled,
  ActivityNavIcon,
  ActivityNavIconFilled,
  MessagesNavIcon,
  MessagesNavIconFilled,
  ToolsNavIcon,
  ToolsNavIconFilled,
} from './NavIcons';

export type { NavTabId };

/**
 * Flags describing which overlay is currently open in the AppContent shell.
 * The active nav tab is derived from these rather than the router, because the
 * SPA switches views by React state, not URL.
 */
export type NavOverlayState = {
  toolsOpen: boolean;
  messagesOpen: boolean;
  activityOpen: boolean;
};

/**
 * Resolve the active tab from app state. Explore is the fallback (the map with
 * no overlay open). Named helper with early returns per project convention
 * (no nested ternaries).
 */
export function getActiveTab(state: NavOverlayState): NavTabId {
  if (state.toolsOpen) return 'tools';
  if (state.messagesOpen) return 'messages';
  if (state.activityOpen) return 'activity';
  return 'explore';
}

type NavIconComponent = React.FC<{ className?: string }>;

// `IconFilled` is the active-state glyph shown by the shared cross-fade in NavTab.
const TABS: Array<{
  id: NavTabId;
  labelKey: string;
  Icon: NavIconComponent;
  IconFilled?: NavIconComponent;
}> = [
  {
    id: 'explore',
    labelKey: 'newLandingPage.nav.explore',
    Icon: ExploreNavIcon,
    IconFilled: ExploreNavIconFilled,
  },
  {
    id: 'activity',
    labelKey: 'newLandingPage.nav.activity',
    Icon: ActivityNavIcon,
    IconFilled: ActivityNavIconFilled,
  },
  {
    id: 'messages',
    labelKey: 'newLandingPage.nav.messages',
    Icon: MessagesNavIcon,
    IconFilled: MessagesNavIconFilled,
  },
  {
    id: 'tools',
    labelKey: 'newLandingPage.nav.tools',
    Icon: ToolsNavIcon,
    IconFilled: ToolsNavIconFilled,
  },
];

type NavigationProps = {
  activeTab: NavTabId;
  onSelect: (tab: NavTabId) => void;
  /** Unread count for the Activity badge. <= 0 hides it. 0 for now (no backend). */
  activityCount?: number;
  /** Unread direct-message count for the Messages badge. <= 0 hides it. */
  messagesCount?: number;
  /** Optional bubble rendered above the pill, anchored to the Activity tab via
   *  the --jaakko-nav-activity-x CSS variable (e.g. the JAA-315 success toast).
   *  Passing one also lifts the nav over the desktop nav-modal cards, which
   *  otherwise outrank it and bury the bubble (see Navigation.scss). */
  toast?: React.ReactNode;
};

const ACTIVITY_INDEX = TABS.findIndex((tab) => tab.id === 'activity');

/**
 * Global floating pill navigation (JAA-266). Bottom-center on every viewport;
 * layout (icon-above-label on mobile, icon-beside-label on laptop/desktop) is
 * handled in Navigation.scss. Mounted once in the AppContent shell, auth-gated
 * by the caller.
 */
const Navigation: React.FC<NavigationProps> = ({
  activeTab,
  onSelect,
  activityCount = 0,
  messagesCount = 0,
  toast,
}) => {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [marker, setMarker] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  // Activity tab's center X relative to the nav box; anchors the toast tail.
  const [activityX, setActivityX] = useState<number | null>(null);
  // Hold the slide transition off until the marker is first placed, so it
  // doesn't animate in from the pill's left edge on mount.
  const [animated, setAnimated] = useState(false);

  const activeIndex = TABS.findIndex((tab) => tab.id === activeTab);

  // Measure the active tab against the pill's padding-box origin (getBoundingClientRect
  // is layout-unit agnostic, so it works for equal-width mobile tabs and
  // content-width laptop/desktop tabs alike). Re-runs on tab change; a
  // ResizeObserver below re-measures on viewport/label reflow.
  const measure = () => {
    const pill = pillRef.current;
    const tab = tabRefs.current[activeIndex];
    if (!pill || !tab) return;
    const pillRect = pill.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const cs = window.getComputedStyle(pill);
    // Overlay the marker exactly on the active tab's box. The marker's top/left
    // (0,0) resolve to the pill's padding-box edge (just inside the border), so
    // subtract only the border — never the padding (the tab already sits inset
    // by the padding, and double-counting it shifted the marker, which over-hung
    // the first tab and left a gap on the last).
    const borderLeft = parseFloat(cs.borderLeftWidth) || 0;
    const borderTop = parseFloat(cs.borderTopWidth) || 0;
    setMarker({
      left: Math.round(tabRect.left - pillRect.left - borderLeft),
      top: Math.round(tabRect.top - pillRect.top - borderTop),
      width: Math.round(tabRect.width),
      height: Math.round(tabRect.height),
    });
    const nav = navRef.current;
    const activityTab = tabRefs.current[ACTIVITY_INDEX];
    if (nav && activityTab) {
      const navRect = nav.getBoundingClientRect();
      const actRect = activityTab.getBoundingClientRect();
      setActivityX(Math.round(actRect.left + actRect.width / 2 - navRect.left));
    }
  };

  useLayoutEffect(() => {
    measure();
    const raf = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  useEffect(() => {
    const pill = pillRef.current;
    if (!pill || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(pill);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  return (
    <nav
      ref={navRef}
      className={`jaakko-nav jaakko-nav--${activeTab}${toast ? ' jaakko-nav--toast' : ''}`}
      aria-label={t('newLandingPage.nav.label')}
      style={
        activityX !== null
          ? ({ '--jaakko-nav-activity-x': `${activityX}px` } as React.CSSProperties)
          : undefined
      }
    >
      {toast}
      <div data-map-occluder="navigation" className="jaakko-nav__pill" ref={pillRef}>
        <span
          className={`jaakko-nav__marker${animated ? ' jaakko-nav__marker--animated' : ''}`}
          aria-hidden="true"
          style={
            marker
              ? {
                  width: marker.width,
                  height: marker.height,
                  transform: `translate(${marker.left}px, ${marker.top}px)`,
                }
              : { opacity: 0 }
          }
        />
        {TABS.map(({ id, labelKey, Icon, IconFilled }, index) => (
          <NavTab
            key={id}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            id={id}
            label={t(labelKey)}
            icon={
              <Icon
                className={`jaakko-nav__icon-glyph${
                  IconFilled ? ' jaakko-nav__icon-glyph--outline' : ''
                }`}
              />
            }
            iconFilled={
              IconFilled ? (
                <IconFilled className="jaakko-nav__icon-glyph jaakko-nav__icon-glyph--filled" />
              ) : undefined
            }
            isActive={activeTab === id}
            onSelect={onSelect}
            badgeCount={
              id === 'activity'
                ? activityCount
                : id === 'messages'
                  ? messagesCount
                  : 0
            }
            badgeAriaLabel={
              (id === 'activity' && activityCount > 0) ||
              (id === 'messages' && messagesCount > 0)
                ? t('newLandingPage.nav.unread', {
                    label: t(labelKey),
                    count: id === 'activity' ? activityCount : messagesCount,
                  })
                : undefined
            }
          />
        ))}
      </div>
    </nav>
  );
};

export default Navigation;
